import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Categories() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", parent_id: "", group: "Interior" });
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/categories").then((r) => setCategories(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const topCats = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);
  const groups = useMemo(() => {
    const g = {};
    topCats.forEach((c) => {
      g[c.group] = g[c.group] || [];
      g[c.group].push({ ...c, subs: categories.filter((s) => s.parent_id === c.id) });
    });
    return g;
  }, [categories, topCats]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/categories", { name: form.name, parent_id: form.parent_id || null, group: form.parent_id ? null : form.group });
      toast.success("Category added");
      setForm({ name: "", parent_id: "", group: "Interior" });
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this category?")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Category removed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="categories-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#1B2A4A]">Categories</h1>
        <p className="text-sm text-[#475569] mt-1">Master category &amp; subcategory list</p>
      </div>

      {isAdmin && (
        <form onSubmit={add} className="bg-white border border-[#E2E8F0] rounded-xl p-5 flex items-end gap-3 flex-wrap">
          <div className="space-y-1.5 flex-1 min-w-[200px]">
            <Label>Name</Label>
            <Input data-testid="new-category-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Solar Installation" />
          </div>
          <div className="space-y-1.5">
            <Label>Parent (makes it a subcategory)</Label>
            <Select value={form.parent_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, parent_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="new-category-parent" className="w-60"><SelectValue placeholder="None (top-level)" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">None (top-level)</SelectItem>
                {topCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!form.parent_id && (
            <div className="space-y-1.5">
              <Label>Group</Label>
              <Select value={form.group} onValueChange={(v) => setForm((f) => ({ ...f, group: v }))}>
                <SelectTrigger data-testid="new-category-group" className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["Interior", "Exterior", "Health & Wellness", "Other Services"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button data-testid="add-category-button" type="submit" className="bg-[#2E7BD6] hover:bg-[#2568b8] text-white">
            <Plus size={15} className="mr-1.5" /> Add Category
          </Button>
        </form>
      )}

      <div className="space-y-5" data-testid="categories-list">
        {Object.entries(groups).map(([group, cats]) => (
          <div key={group} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-[#1B2A4A] text-white flex items-center gap-2">
              <Layers size={15} />
              <span className="font-semibold text-sm tracking-wide">{group}</span>
            </div>
            <div className="divide-y divide-[#F1F5F9]">
              {cats.map((c) => (
                <div key={c.id} className="px-5 py-4" data-testid={`category-${c.slug}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[#0F172A]">{c.name}</span>
                    {isAdmin && (
                      <button data-testid={`delete-category-${c.id}`} onClick={() => remove(c.id)} className="text-[#94A3B8] hover:text-red-600 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {c.subs.map((s) => (
                      <span key={s.id} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#475569] bg-[#F5F7FA] border border-[#E2E8F0] rounded-full px-2.5 py-1">
                        {s.name}
                        {isAdmin && (
                          <button data-testid={`delete-subcategory-${s.id}`} onClick={() => remove(s.id)} className="text-[#94A3B8] hover:text-red-600">
                            <X_Icon />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const X_Icon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
);
